import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { store } from './store';
import theme from './theme';
import MainLayout from './components/layout/MainLayout';

// Import API service early to register global axios interceptors
import './services/api';

// Pages
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ReferralRedirect from './pages/auth/ReferralRedirect';
import ForgotPassword from './pages/auth/ForgotPassword';
import RespondentDashboard from './pages/respondent/Dashboard';
import ClientDashboard from './pages/client/Dashboard';
import SurveyBuilder from './pages/client/SurveyBuilder';
import SurveyPreview from './pages/client/SurveyPreview';
import TestSurveyPages from './pages/client/TestSurveyPages';
import AdminDashboard from './pages/admin/Dashboard';
import AdminModeration from './pages/admin/Moderation';
import AdminNotifications from './pages/admin/Notifications';
import AdminProfile from './pages/admin/Profile';
import AdminProfileSurveys from './pages/admin/ProfileSurveys';
import AdminSupportChats from './pages/admin/SupportChats';
import CreateTestAccount from './pages/admin/CreateTestAccount';
import ClientNotifications from './pages/client/Notifications';
import ClientProfile from './pages/client/Profile';
import ClientIntegrations from './pages/client/Integrations';
import AssignedSurveys from './pages/client/AssignedSurveys';
import ClientSupportChat from './pages/client/SupportChat';
import ProtectedRoute from './components/ProtectedRoute';
import PublicSurvey from './pages/public/PublicSurvey';

// Initialize mobile push notifications if running in Capacitor
const isMobileApp = window.location.protocol === 'capacitor:' || 
                    window.location.protocol === 'ionic:' ||
                    window.Capacitor?.isNativePlatform?.() === true;

console.log('[App.js] Protocol:', window.location.protocol);
console.log('[App.js] isMobileApp:', isMobileApp);
console.log('[App.js] Capacitor available:', !!window.Capacitor);

if (isMobileApp) {
  console.log('[App.js] Initializing mobile push notifications...');
  import('./mobile-init').then(({ initializeMobilePushNotifications }) => {
    initializeMobilePushNotifications();
  }).catch(err => {
    console.error('[App.js] Failed to load mobile-init:', err);
  });
} else {
  console.log('[App.js] Not a mobile app, skipping push notification setup');
}

// Set global flag
window.isMobileApp = isMobileApp;

// Inner component to access useNavigate
function AppRoutes() {
  const navigate = useNavigate();

  useEffect(() => {
    // Handle session expiration event from API interceptor
    const handleSessionExpired = () => {
      console.log('[App.js] Session expired, redirecting to login...');
      navigate('/login', { replace: true });
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    if (isMobileApp) {
      console.log('[App.js] Setting up global navigate for mobile');
      import('./mobile-init').then(({ setGlobalNavigate }) => {
        setGlobalNavigate(navigate);
      });

      // Check for pending navigation on mount
      const pendingNav = localStorage.getItem('pendingNavigation');
      if (pendingNav) {
        console.log('[App.js] Found pending navigation on mount:', pendingNav);
        localStorage.removeItem('pendingNavigation');
        
        // Check if user is logged in
        const token = localStorage.getItem('token');
        if (token) {
          // User logged in - navigate to pending route
          setTimeout(() => {
            console.log('[App.js] Executing pending navigation to:', pendingNav);
            navigate(pendingNav);
          }, 1000); // Increased delay to ensure app is fully loaded
        } else {
          // User not logged in - redirect to login with return path
          console.log('[App.js] User not logged in, redirecting to login');
          setTimeout(() => {
            navigate('/login', { state: { from: pendingNav } });
          }, 500);
        }
      }
    }

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [navigate]);

  return (
    <MainLayout>
      <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Navigate to="/register/respondent" replace />} />
              <Route path="/register/:role" element={<Register />} />
              <Route path="/ref/:code" element={<ReferralRedirect />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              
              {/* Public survey route - no authentication required */}
              <Route path="/public/survey/:id" element={<PublicSurvey />} />
              
              {/* Respondent routes */}
              <Route 
                path="/respondent/*" 
                element={
                  <ProtectedRoute allowedRoles={['respondent']}>
                    <RespondentDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Client routes */}
              <Route 
                path="/client/dashboard" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <ClientDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/survey-builder" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <SurveyBuilder />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/survey-builder/:id" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <SurveyBuilder />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/survey/:id/preview" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <SurveyPreview />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/survey/:id/test" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <TestSurveyPages />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/profile" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <ClientProfile />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/client/assigned"
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <AssignedSurveys />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client/integrations"
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <ClientIntegrations />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/client/notifications" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <ClientNotifications />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/support" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <ClientSupportChat />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin routes */}
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/moderation" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminModeration />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/profile-surveys" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminProfileSurveys />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/support-chats" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminSupportChats />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/notifications" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminNotifications />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/profile" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminProfile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/create-test-account" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CreateTestAccount />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/survey-builder" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <SurveyBuilder />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/survey-builder/:id" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <SurveyBuilder />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/survey/:id/test" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <TestSurveyPages />
                  </ProtectedRoute>
                } 
              />
            </Routes>
          </MainLayout>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AppRoutes />
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;